// Room warnings, overlap counting and duration ceiling, ported from v1.

import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { COUNT_AS_OVERLAPPING, INFO_CONSTANTS, ROOM_STATUS } from 'features/Home/constants/infoConstants'
import type { DisabledInterval, IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { InfoService } from 'features/Home/services/infoService'

type Args = {
  rooms: IPatientRoom[]
  schedules: IPatientSchedule[]
  disabledIntervals: DisabledInterval[]
  dayInterval: { min: string; max: string }
  timeZone: string
  editedAppointmentId?: number
}

export const useVisitRules = ({
  rooms,
  schedules,
  disabledIntervals,
  dayInterval,
  timeZone,
  editedAppointmentId,
}: Args) => {
  const [selectedRoom, setSelectedRoom] = useState<IPatientRoom | null>(null)
  const [overlappingEvents, setOverlappingEvents] = useState(0)
  const [isInsideDisabledInterval, setIsInsideDisabledInterval] = useState(false)

  const calculateMaxDuration = useCallback(
    (selectedTime?: Date | null): number => {
      if (!dayInterval || !selectedTime) return 999

      const selected = dayjs(`2000-01-01 ${dayjs(selectedTime).format('HH:mm')}`)
      const end = dayjs(`2000-01-01 ${dayInterval.max}`)

      // A time past the end of the day must not yield a negative ceiling.
      return Math.max(1, Math.min(end.diff(selected, 'minute'), 999))
    },
    [dayInterval],
  )

  const updateOverlaps = useCallback(
    (room: IPatientRoom | null, start?: Date, end?: Date) => {
      if (!room || !start || !end) return

      const insideDisabled = disabledIntervals.some((interval) =>
        InfoService.checkIsOverlappingIntervals(interval, start, end, `${room.label}/${room.value}`),
      )
      setIsInsideDisabledInterval(insideDisabled)
      if (insideDisabled) return

      const roomEvents = schedules
        .filter(
          (schedule) =>
            schedule.room?.value === room.value &&
            schedule.id !== editedAppointmentId &&
            COUNT_AS_OVERLAPPING.includes(schedule.status.code),
        )
        .map((item) => ({
          id: item.id,
          title: item.patient.name,
          start: new Date(`${item.date} ${item.start_time}`),
          end: new Date(`${item.date} ${item.end_time}`),
          resourceId: item?.room?.label,
          status: item.status,
        }))

      const { nrOfOverlappingEvents } = InfoService.getNumberOfOverlappingIntervals(
        { start, end, resourceId: room.label },
        roomEvents,
        room.label,
      )
      setOverlappingEvents(nrOfOverlappingEvents)
    },
    [disabledIntervals, editedAppointmentId, schedules],
  )

  const selectRoom = useCallback(
    (roomValue: string, start?: Date, end?: Date) => {
      const room = rooms.find((item) => String(item.value) === String(roomValue)) ?? null
      setSelectedRoom(room)
      updateOverlaps(room, start, end)
      return room
    },
    [rooms, updateOverlaps],
  )

  const roomErrorMessage = useMemo(() => {
    if (!selectedRoom) return undefined

    const applyRoomRule = !InfoService.checkIfCurrentDateOverlapDisabledIntervals(
      disabledIntervals,
      selectedRoom,
      dayInterval,
      timeZone,
    )
    const excluded = [ROOM_STATUS.FREE, ROOM_STATUS.CLEANUP, ROOM_STATUS.BUSY]

    if (selectedRoom.status && !excluded.includes(selectedRoom.status.code) && applyRoomRule) {
      return INFO_CONSTANTS.BUSY_ROOM_WARNING
    }

    if (overlappingEvents > selectedRoom.max_patients && !selectedRoom.is_waiting_room) {
      return `You reached the max number of ${
        selectedRoom.max_patients || 1
      } patients at the same time for this room.`
    }

    return undefined
  }, [dayInterval, disabledIntervals, overlappingEvents, selectedRoom, timeZone])

  const reset = useCallback(() => {
    setSelectedRoom(null)
    setOverlappingEvents(0)
    setIsInsideDisabledInterval(false)
  }, [])

  return {
    selectedRoom,
    setSelectedRoom,
    selectRoom,
    updateOverlaps,
    overlappingEvents,
    isInsideDisabledInterval,
    roomErrorMessage,
    calculateMaxDuration,
    reset,
  }
}
